import { watch, onMounted, h } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuth } from './auth';
import { useAppState } from './app-state';
import { useCategories } from './categories';
import { usePlatform, usePlatformUtils } from './platform';
import { RESERVED_SLUGS } from '/app/lib/platform';
import { useVideoProcessing } from './video-processing';
import { useAccessGate } from './access-gate';
import { useUploadQueue } from './upload-queue';
import { useBus } from './bus';
import { ensureNotificationToken } from './notification-token';
import { useNotifier } from './notifier';
import { useQueryClient } from '@tanstack/vue-query';
import { usePageContext } from './data';
import { sentry } from '/app/plugins/sentry';
import { api } from '/app/plugins/services';
import { bus } from '/app/plugins/bus';
import { uuid } from '/app/utils/helpers';
import { Session } from '/app/utils/auth';
import { config } from '/app/config';
import Storage from '/app/utils/storage';
import Cookie from '/app/utils/cookie';
import { detectOS, isStandalonePWA } from '/app/utils/platform-os';
import { trackSignupIfNew } from '/app/utils/signup-tracking';

export function useAppInit() {
    const route = useRoute();
    const router = useRouter();
    const { auth, loadMe } = useAuth();
    const appState = useAppState();
    const { fetchAll: fetchAllCategories } = useCategories();
    const { platform, contextPlatform } = usePlatform();
    const { isUserPlatform } = usePlatformUtils();
    const { open: notify } = useNotifier();
    const { on, once, emit: busEmit } = useBus();
    const { platform: pageContextPlatform } = usePageContext();
    const queryClient = useQueryClient();
    const videoProcessing = useVideoProcessing();
    const accessGate = useAccessGate();
    // Instantiate here so the shared scope stays alive for the whole app session:
    // an upload can then keep running while the UploadFile component that started
    // it unmounts (modal closed / navigated away).
    useUploadQueue();

    // ── Page Context (SSR hydration) ────────────────────────────────
    if (pageContextPlatform?.slug) {
        queryClient.setQueryData(['platform:by-slug', pageContextPlatform.slug], pageContextPlatform);
    }

    if (typeof window !== 'undefined') {
        window.heap?.addEventProperties({
            'App Version': config.app.version,
            // `Platform` is the platform slug (a BigNerve tenant/community — not
            // the OS). Kept for backwards compatibility with existing Heap
            // reports; prefer `Platform Slug` for new segments and migrate
            // dashboards over, then retire this.
            'Platform': pageContextPlatform?.slug,
            'Platform Slug': pageContextPlatform?.slug,
            'OS Platform': detectOS(), // 'ios' | 'android' | 'web'
            'Is PWA': isStandalonePWA(),
        });
    }

    // ── Auth ────────────────────────────────────────────────────────
    function fetchCurrentUser() {
        if (Session.exists() && !auth.authenticated) {
            loadMe().then((account) => {
                const user = account.user;
                busEmit('user:logged-in', user);

                Storage.set('user', {
                    id: user.id,
                    email: user.email,
                    first_name: user.first_name,
                    last_name: user.last_name,
                    screen_name: user.screen_name,
                });

                if (typeof window !== 'undefined') {
                    window.heap?.identify(user.screen_name);

                    window.heap?.addUserProperties({
                        email: user.email,
                        name: `${user.first_name} ${user.last_name}`,
                        handle: user.screen_name,
                        scout: user.scout_referrer,
                        id: user.id,
                        'OS Platform': detectOS(),
                    });

                    window.heap?.addEventProperties({
                        'Logged In': true,
                    });

                    trackSignupIfNew(user);
                }
            }).catch(() => {
                sentry.addBreadcrumb({
                    category: 'App',
                    message: 'Error logging in',
                    level: 'error',
                });
            });
        }
    }

    // ── Context User ────────────────────────────────────────────────
    async function fetchContextUser() {
        const slug = route.params.platformSlug;

        if (!isUserPlatform.value || !slug || RESERVED_SLUGS.includes(slug)) {
            sentry.addBreadcrumb({
                category: 'App',
                message: `Resetting user state: ${slug || 'no slug'}`,
                level: 'info',
            });

            appState.resetUser();
            return;
        }

        sentry.addBreadcrumb({
            category: 'App',
            message: `Fetching user context for ${slug}`,
            level: 'info',
        });

        const currentAccount = appState.contextUser;
        const isDifferentSlug = !currentAccount?.user || currentAccount.user.screen_name !== slug;

        if (!isDifferentSlug) {
            return currentAccount;
        }

        try {
            sentry.addBreadcrumb({
                category: 'App',
                message: `Current user slug: ${currentAccount?.user?.screen_name}`,
                level: 'info',
            });

            const newAccount = await appState.fetchUser(slug);

            if (!newAccount?.user) {
                sentry.addBreadcrumb({
                    category: 'App',
                    message: `Empty user payload for ${slug}`,
                    level: 'warning',
                });

                busEmit('account:not-found', { slug });
                return;
            }

            if (newAccount.user.screen_name !== slug) {
                sentry.addBreadcrumb({
                    category: 'App',
                    message: `Redirecting from ${slug} to ${newAccount.user.screen_name}`,
                    level: 'info',
                });

                console.log(`[app] Redirecting from ${slug} to ${newAccount.user.screen_name}`);
                router.push({ ...route, params: { ...route.params, platformSlug: newAccount.user.screen_name }, replace: true });
            }

            return newAccount;
        } catch (error) {
            sentry.addBreadcrumb({
                category: 'App',
                message: `Error fetching user data for ${slug}`,
                level: 'error',
            });

            sentry.withScope((scope) => {
                scope.setExtra('slug', slug);
                sentry.captureException(error);
            });

            busEmit('account:not-found', { slug });
        }
    }

    // ── Scout ───────────────────────────────────────────────────────
    function handleScout() {
        if (route.query.scout_id) {
            Cookie.set('scout_id', route.query.scout_id, true, 270);
        }
    }

    // ── First Access ────────────────────────────────────────────────
    function init() {
        const firstAccess = !Cookie.has('fingerprint');

        if (firstAccess) {
            Cookie.set('fingerprint', uuid(), true, Infinity);
        }

        if (firstAccess && Session.isAuthenticated()) {
            api.analytics.track('FirstAccess').catch(() => {
                sentry.addBreadcrumb({
                    category: 'App',
                    message: 'Error tracking first access',
                    level: 'error',
                });
            });
        }
    }

    // ── Bus Listeners ───────────────────────────────────────────────
    once('user:logged-in', (user) => {
        const scope = sentry.getCurrentScope();
        scope.setUser({
            name: `${user.first_name} ${user.last_name}`,
            email: user.email,
            handle: user.screen_name,
            id: user.id,
            scout_referrer: user.scout_referrer,
        });

        // Warm the Pusher authorisation token. Channels also ensure it before
        // subscribing, so this only saves them the round trip.
        ensureNotificationToken().catch(() => {
            sentry.addBreadcrumb({
                category: 'App',
                message: 'Error setting notification token',
                level: 'error',
            });
        });
    });

    on('user:update-details', () => {
        fetchCurrentUser();
    });

    on('platform:change', (p) => {
        appState.fetchCategories(p);
    });

    on('account:not-found', ({ slug }) => {
        router.push({ name: 'home.challenges' });

        notify({
            title: 'User not found!',
            message: `The username "**${slug}**" does not exist. Check for typos, try a different link, or browse the Nerve Boards.`,
            cancelText: 'Ok',
            hideConfirm: true,
        });
    });

    // ── Watchers ────────────────────────────────────────────────────
    watch(
        () => route.params.platformSlug,
        (newSlug, oldSlug) => {
            if (newSlug !== oldSlug) {
                fetchContextUser();
            }
        },
    );

    watch(
        () => route.fullPath,
        () => {
            fetchCurrentUser();
            handleScout();
        },
    );

    // ── Access Gate ─────────────────────────────────────────────────
    // Registered during setup so the listeners bind to this component's scope.
    accessGate.watchVisibility();

    // ── Lifecycle ───────────────────────────────────────────────────
    fetchAllCategories(platform.value.id);

    onMounted(() => {
        init();
        handleScout();

        // The edge gate only runs on a document request. Re-verify at boot so a
        // document served from a cache that predates the gate fix (or any cache
        // we can't reach) still gets re-evaluated.
        accessGate.check({ force: true });

        bus('platform:change', platform.value);

        // Resume any video downloads still being enabled from a previous session
        // (the auth token is available synchronously from the cookie at boot).
        if (Session.exists()) {
            videoProcessing.resumeAll();
        }

        router.isReady().then(() => {
            fetchCurrentUser();
            fetchContextUser();
        });
    });

   
}
